import type { Rng } from "../../rng";
import {
	type BaseState,
	type GameCategory,
	type GameDefinition,
	type GameId,
	type MoveOutcome,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../../types";

/**
 * A whole family of games is the same game underneath: both players see the
 * same prompt at the same time, tap one of a few answers, and the clock decides
 * when to move on. Times tables, spelling, sorting, geography, would-you-rather
 * — all of them are this, with a different round generator.
 */
export interface ChoiceRound {
	id: string;
	/** The question, in a 10-year-old's words. */
	prompt: string;
	/** Something big to show above the prompt — an emoji, a sum, a word. */
	glyph?: string;
	choices: string[];
	/** Index into `choices`, or `-1` when there is no right answer. */
	answer: number;
	/** Shown during the reveal, to explain the answer. */
	note?: string;
	/**
	 * Tones for the client to play, as `"440,660"`. Used by the ear-training
	 * games so they need no audio files at all.
	 */
	tones?: string;
}

export interface ChoiceAnswer {
	/** `-1` when hidden from the other player. */
	choice: number;
	at: number;
}

export interface ChoiceDuelState<Id extends GameId = GameId> extends BaseState {
	kind: Id;
	rounds: ChoiceRound[];
	index: number;
	phase: "question" | "reveal";
	/** When the open round closes, or when the reveal ends. */
	endsAt: number;
	/**
	 * How long each question stays open. Carried on the state so a client can
	 * draw the countdown as a fraction of the whole — `endsAt` alone only says
	 * when the round closes, not how long it was.
	 */
	questionMs: number;
	answers: [ChoiceAnswer | null, ChoiceAnswer | null];
	/** Points won on the round being revealed. */
	lastAward: [number, number];
}

export interface ChoiceMove {
	choice: number;
}

/**
 * `speed` — a right answer scores, and the fastest right answer scores more.
 * `match` — there is no right answer; you both score for agreeing.
 */
export type ChoiceScoring = "speed" | "match";

export interface ChoiceDuelOptions<Id extends GameId> {
	id: Id;
	category: GameCategory;
	title: string;
	emoji: string;
	blurb: string;
	howTo: readonly string[];
	rounds: number;
	questionMs: number;
	revealMs?: number;
	scoring?: ChoiceScoring;
	correctPoints?: number;
	speedBonus?: number;
	generate(rng: Rng, count: number): ChoiceRound[];
}

const HIDDEN_ROUND: ChoiceRound = { id: "", prompt: "", choices: [], answer: -1 };

export function createChoiceDuel<Id extends GameId>(
	options: ChoiceDuelOptions<Id>,
): GameDefinition<ChoiceDuelState<Id>, ChoiceMove> {
	const revealMs = options.revealMs ?? 3000;
	const scoring = options.scoring ?? "speed";
	const correctPoints = options.correctPoints ?? 1;
	const speedBonus = options.speedBonus ?? 1;

	function score(state: ChoiceDuelState<Id>, now: number): ChoiceDuelState<Id> {
		const round = state.rounds[state.index];
		const award: [number, number] = [0, 0];

		if (round) {
			if (scoring === "match") {
				const [first, second] = state.answers;
				// Agreeing is the whole point: both score, or neither does.
				if (first && second && first.choice === second.choice) {
					award[0] = correctPoints;
					award[1] = correctPoints;
				}
			} else {
				const correct: { player: PlayerIndex; at: number }[] = [];
				for (const player of [0, 1] as const) {
					const answer = state.answers[player];
					if (answer && answer.choice === round.answer) correct.push({ player, at: answer.at });
				}
				correct.sort((a, b) => a.at - b.at);
				correct.forEach((entry, rank) => {
					award[entry.player] = correctPoints + (rank === 0 ? speedBonus : 0);
				});
			}
		}

		return {
			...state,
			scores: [state.scores[0] + award[0], state.scores[1] + award[1]],
			lastAward: award,
			phase: "reveal",
			endsAt: now + revealMs,
		};
	}

	function advance(state: ChoiceDuelState<Id>, now: number): ChoiceDuelState<Id> {
		const index = state.index + 1;
		if (index >= state.rounds.length) {
			return { ...state, result: resultFromScores(state.scores), endsAt: now };
		}
		return {
			...state,
			index,
			phase: "question",
			endsAt: now + options.questionMs,
			answers: [null, null],
			lastAward: [0, 0],
		};
	}

	return {
		id: options.id,
		category: options.category,
		title: options.title,
		emoji: options.emoji,
		blurb: options.blurb,
		howTo: options.howTo,

		create(rng, now) {
			return {
				kind: options.id,
				rounds: options.generate(rng, options.rounds),
				index: 0,
				phase: "question",
				endsAt: now + options.questionMs,
				questionMs: options.questionMs,
				answers: [null, null],
				lastAward: [0, 0],
				// Nobody's "turn" — both players answer at once.
				turn: null,
				scores: [0, 0],
				result: null,
			};
		},

		parseMove(raw) {
			if (typeof raw !== "object" || raw === null) return null;
			const choice = (raw as { choice?: unknown }).choice;
			if (typeof choice !== "number" || !Number.isInteger(choice)) return null;
			if (choice < 0 || choice > 7) return null;
			return { choice };
		},

		apply(state, player, move, now): MoveOutcome<ChoiceDuelState<Id>> {
			if (state.result) return reject("This game is already finished.");
			if (state.phase !== "question") return reject("Hold on — the answer is showing.");
			if (state.answers[player]) return reject("You've already picked an answer.");

			const round = state.rounds[state.index];
			if (!round || move.choice >= round.choices.length) {
				return reject("That isn't one of the answers.");
			}

			const answers: [ChoiceAnswer | null, ChoiceAnswer | null] = [...state.answers];
			answers[player] = { choice: move.choice, at: now };

			const next = { ...state, answers };
			// Both in: reveal straight away rather than watching the timer run out.
			return { ok: true, state: answers[0] && answers[1] ? score(next, now) : next };
		},

		legalMoves(state, player) {
			if (state.result || state.phase !== "question" || state.answers[player]) return [];
			const round = state.rounds[state.index];
			if (!round) return [];
			return round.choices.map((_, choice) => ({ choice }));
		},

		tick(state, now) {
			if (state.result || now < state.endsAt) return null;
			return state.phase === "question" ? score(state, now) : advance(state, now);
		},

		nextTickAt(state) {
			return state.result ? null : state.endsAt;
		},

		redact(state, viewer) {
			return {
				...state,
				rounds: state.rounds.map((round, i) => {
					if (i > state.index) return HIDDEN_ROUND;
					// The answer key only goes out once the round is closed — and the
					// note goes with it, because some banks write it as "a dolphin —
					// Ocean", which names the right choice in as many words.
					if (i === state.index && state.phase === "question") {
						return { ...round, answer: -1, note: undefined };
					}
					return round;
				}),
				answers: state.answers.map((answer, i) => {
					if (answer === null) return null;
					if (state.phase === "reveal" || i === viewer) return answer;
					// You can see *that* they answered, not what they picked.
					return { choice: -1, at: answer.at };
				}) as [ChoiceAnswer | null, ChoiceAnswer | null],
			};
		},
	};
}

/** Shuffles the choices and reports where the right answer ended up. */
export function shuffledRound(
	rng: Rng,
	round: Omit<ChoiceRound, "answer"> & { correct: string },
): ChoiceRound {
	const choices = rng.shuffle(round.choices);
	return {
		id: round.id,
		prompt: round.prompt,
		glyph: round.glyph,
		note: round.note,
		tones: round.tones,
		choices,
		answer: choices.indexOf(round.correct),
	};
}
